import * as Types from '@aeppic/types'
import { EditableDocument, Form } from '.'
import { InMemoryLog as SharedInMemoryLog, InMemoryLogOptions as SharedInMemoryLogOptions } from '@aeppic/shared/log'

export {
  SimpleConsoleLogger,
  LogLevels,
  VoidLogger,
  buildLogger,
  setDefaultLogLevel,
} from '@aeppic/shared/log'

export type {
  LogFunction,
  BaseLogger,
  Logger,
  LoggerOptions,
  LogEntry,
  LogLine,
  InMemoryLogOptions,
} from '@aeppic/shared/log'

const SENSITIVE_TAG = 'sensitive'
const REDACTED_SENSITIVE = '<<REDACTED: sensitive field>>'
const REDACTED_PASSWORD = '<<REDACTED: password field>>'
const REDACTED_FORM_NOT_FOUND = '<<REDACTED: form not found>>'
const REDACTED_LOOKUP_ERROR = '<<REDACTED: form lookup error>>'
const MAX_FIELD_LENGTH = 200

export interface AeppicLogOptions extends SharedInMemoryLogOptions {
  /** Resolve a Form by document's form reference. Required to strip sensitive fields from plain documents. */
  getForm?: (formRef: Types.IdOrReference) => Form | null | undefined
}

function truncateValue(value: any): any {
  if (value === null || value === undefined) return value

  if (typeof value === 'string') {
    if (value.length > MAX_FIELD_LENGTH) {
      return `<<TRUNCATED: ${value.length} chars>> ${value.slice(0, MAX_FIELD_LENGTH)}`
    }
    return value
  }

  if (typeof value === 'object') {
    const json = JSON.stringify(value)
    if (json.length > MAX_FIELD_LENGTH) {
      return `<<TRUNCATED: ${json.length} chars>> ${json.slice(0, MAX_FIELD_LENGTH)}`
    }
    return value
  }

  return value
}

function sanitizeDataFields(data: any, form: Form | null | undefined, sensitiveFields: string[]): any {
  if (!data) return data

  const result: any = {}
  for (const key of Object.keys(data)) {
    if (sensitiveFields.includes(key)) {
      result[key] = REDACTED_SENSITIVE
    } else if (form) {
      const field = form.getField(key)
      if (field?.subType === 'password') {
        result[key] = REDACTED_PASSWORD
      } else {
        result[key] = truncateValue(data[key])
      }
    } else {
      result[key] = truncateValue(data[key])
    }
  }
  return result
}

function createAeppicSanitizer(getForm?: (formRef: Types.IdOrReference) => Form | null | undefined) {
  return (obj: any) => {
    if (!obj) return obj

    if (EditableDocument.isEditableDocument(obj)) {
      const form: Form = obj.form
      let sensitiveFields: string[] = []

      if (form) {
        try {
          sensitiveFields = form.getTaggedFields([SENSITIVE_TAG])
        } catch {
          // Form doesn't support getTaggedFields
        }
      }

      if (sensitiveFields.length > 0) {
        const doc = obj.cloneAsDocument()
        doc.data = sanitizeDataFields(doc.data, form, sensitiveFields)
        return doc
      }

      // No sensitive fields — still truncate long values and redact passwords
      return { id: obj.id, f: obj.f, data: sanitizeDataFields(obj.data, form, []) }
    }

    // Plain document — needs form lookup
    if (obj.id && obj.f && obj.data) {
      let sensitiveFields: string[] = []
      let resolvedForm: Form | null = null

      if (getForm) {
        try {
          resolvedForm = getForm(obj.f)
          if (resolvedForm) {
            sensitiveFields = resolvedForm.getTaggedFields([SENSITIVE_TAG])
          } else {
            // Form not found — redact all data fields to be safe
            const redactedData: any = {}
            for (const key of Object.keys(obj.data)) {
              redactedData[key] = REDACTED_FORM_NOT_FOUND
            }
            return { id: obj.id, f: obj.f, data: redactedData }
          }
        } catch {
          const redactedData: any = {}
          for (const key of Object.keys(obj.data)) {
            redactedData[key] = REDACTED_LOOKUP_ERROR
          }
          return { id: obj.id, f: obj.f, data: redactedData }
        }
      }

      return { id: obj.id, f: obj.f, data: sanitizeDataFields(obj.data, resolvedForm, sensitiveFields) }
    }

    return obj
  }
}

/**
 * InMemoryLog with Aeppic-aware sanitizer.
 * - Clones EditableDocuments and plain documents before storing
 * - Strips fields tagged `<sensitive>` in the form definition
 * - When form can't be resolved, redacts ALL data fields
 *
 * Pass `getForm` to enable sensitive-field stripping for plain documents.
 * EditableDocuments already carry their form reference.
 */
export class InMemoryLog extends SharedInMemoryLog {
  constructor(options: AeppicLogOptions = {}) {
    super({
      ...options,
      sanitize: options.sanitize ?? createAeppicSanitizer(options.getForm),
    })
  }
}
